// Session + vault state for the whole app.
//
// Two gates stand between a visitor and their journal, and they are deliberately separate:
//
//   1. Authentication — do we have a valid JWT? Establishes *who* you are.
//   2. Vault unlock   — do we hold your DEK? Establishes whether we can *read* anything.
//
// The previous web client collapsed these by handing every session a key derived from a constant,
// which made the vault screens unreachable and meant "encrypted" did not mean what users assumed.
// Keeping them separate is what lets the browser share one account with the iPhone app.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { clearToken, getToken, setToken, SessionExpiredError } from '../api/client';
import {
  fetchCurrentUser,
  fetchVaultKeys,
  login as apiLogin,
  signup as apiSignup,
  storeVaultKeys,
  type OreiaUser,
} from '../api/auth';
import {
  normalizeRecoveryPhrase,
  provisionVault,
  toNonExtractable,
  unwrapWithSecret,
} from '../crypto/vault';
import { clearStoredDEK, loadStoredDEK, storeDEK } from '../vault/dek-store';

export type VaultState =
  /** Still checking the server and IndexedDB. */
  | { status: 'loading' }
  /** Authenticated, no vault on the server yet — first ever sign-in for this account. */
  | { status: 'needs-setup' }
  /** Vault exists on the server but this browser does not hold the key. */
  | { status: 'locked'; hasPasswordWrap: boolean; hasRecoveryWrap: boolean }
  /**
   * Just provisioned: the key works, but the user has not yet confirmed they saved the recovery
   * phrase. Deliberately distinct from 'unlocked' so the router keeps them on the setup screen —
   * the phrase cannot be regenerated, so "shown exactly once" has to mean shown at least once.
   */
  | { status: 'needs-backup'; dek: CryptoKey; recoveryPhrase: string }
  /** We hold the DEK and can read and write entries. */
  | { status: 'unlocked'; dek: CryptoKey }
  /** Something went wrong talking to the vault endpoints. */
  | { status: 'error'; message: string };

interface SignUpInput {
  email: string;
  password: string;
  name?: string;
  inviteCode?: string;
}

interface SessionValue {
  user: OreiaUser | null;
  isLoading: boolean;
  vault: VaultState;

  /**
   * The password from this session's sign-in or sign-up, held in memory only so vault setup can
   * bind the key to it without asking the user to type it again. Null after the vault is created,
   * and for Google accounts, which have no password at all.
   */
  pendingPassword: string | null;

  signIn(email: string, password: string): Promise<void>;
  signUp(input: SignUpInput): Promise<void>;
  /** Consume a ?token=… handed back by the Google OAuth redirect. */
  adoptToken(token: string): Promise<void>;
  signOut(): Promise<void>;

  /**
   * First-time vault creation. Returns the recovery phrase to show exactly once, and leaves the
   * vault in 'needs-backup' until `acknowledgeRecoveryPhrase` is called.
   * `password` null means the recovery phrase becomes the secret for both wraps, which is how iOS
   * provisions a Google account.
   */
  createVault(password: string | null): Promise<string>;
  /** The user says they have saved the phrase; drop it from memory and open the app. */
  acknowledgeRecoveryPhrase(): void;
  unlockWithPassword(password: string): Promise<boolean>;
  unlockWithRecoveryPhrase(phrase: string): Promise<boolean>;
  /** Forget the key on this device without signing out. */
  lockVault(): Promise<void>;
}

const SessionContext = createContext<SessionValue | null>(null);

export function useSession(): SessionValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used inside <SessionProvider>');
  return ctx;
}

/** Convenience for screens that only render once the vault is open. */
export function useDEK(): CryptoKey {
  const { vault } = useSession();
  if (vault.status !== 'unlocked') {
    throw new Error('useDEK called while the vault was locked — render behind <RequireVault>');
  }
  return vault.dek;
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<OreiaUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [vault, setVault] = useState<VaultState>({ status: 'loading' });
  const [pendingPassword, setPendingPassword] = useState<string | null>(null);

  // Guards against a slow vault restore resolving after the user signed out or switched accounts.
  const generation = useRef(0);
  // Read inside callbacks that must not be re-created whenever the user object changes.
  const userRef = useRef<OreiaUser | null>(null);
  userRef.current = user;

  const resolveVault = useCallback(async (userId: string) => {
    const mine = ++generation.current;

    const stored = await loadStoredDEK(userId);
    if (mine !== generation.current) return;
    if (stored) {
      setVault({ status: 'unlocked', dek: stored });
      return;
    }

    try {
      const keys = await fetchVaultKeys();
      if (mine !== generation.current) return;

      const hasVault = Boolean(keys.passwordWrappedDek || keys.recoveryWrappedDek);
      setVault(
        hasVault
          ? {
              status: 'locked',
              hasPasswordWrap: Boolean(keys.passwordWrappedDek),
              hasRecoveryWrap: Boolean(keys.recoveryWrappedDek),
            }
          : { status: 'needs-setup' },
      );
    } catch (err) {
      if (mine !== generation.current) return;
      if (err instanceof SessionExpiredError) {
        setUser(null);
        setVault({ status: 'loading' });
        return;
      }
      setVault({
        status: 'error',
        message: err instanceof Error ? err.message : 'Could not reach your vault.',
      });
    }
  }, []);

  const establishSession = useCallback(
    async (token: string): Promise<OreiaUser> => {
      setToken(token);
      const nextUser = await fetchCurrentUser();
      setUser(nextUser);
      setVault({ status: 'loading' });
      await resolveVault(nextUser.id);
      return nextUser;
    },
    [resolveVault],
  );

  /**
   * Unwrap the DEK from one of the two server-stored wraps and hold it for this browser.
   * Returns false — rather than throwing — when the secret simply doesn't open the vault, because
   * that is an expected outcome the UI needs to render as a message, not an error boundary.
   */
  const unlockUsingSecret = useCallback(
    async (secret: string, slot: 'password' | 'recovery'): Promise<boolean> => {
      const keys = await fetchVaultKeys().catch(() => null);
      if (!keys?.dekSalt) return false;

      const wrapped = slot === 'recovery' ? keys.recoveryWrappedDek : keys.passwordWrappedDek;
      if (!wrapped) return false;

      const dek = await unwrapWithSecret(wrapped, secret, keys.dekSalt);
      if (!dek) return false;

      const sessionKey = await toNonExtractable(dek);
      const currentUser = userRef.current ?? (await fetchCurrentUser().catch(() => null));
      if (currentUser) await storeDEK(currentUser.id, sessionKey);
      setVault({ status: 'unlocked', dek: sessionKey });
      return true;
    },
    [],
  );

  // Restore an existing session on first paint.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const token = getToken();
      if (!token) {
        setIsLoading(false);
        return;
      }
      try {
        const nextUser = await fetchCurrentUser();
        if (cancelled) return;
        setUser(nextUser);
        await resolveVault(nextUser.id);
      } catch {
        if (!cancelled) {
          clearToken();
          setUser(null);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [resolveVault]);

  const signIn = useCallback(
    async (email: string, password: string) => {
      const res = await apiLogin(email, password);
      // Held in memory so a vault that has not been set up yet can bind to this password, and so
      // an existing vault unlocks without a second prompt.
      setPendingPassword(password);
      await establishSession(res.token);
      // The password we just verified is also the vault-wrapping secret, so unlock in the same
      // step. If it doesn't open the vault (e.g. the account was created through Google), we leave
      // the locked state in place and the unlock screen explains the options.
      await unlockUsingSecret(password, 'password');
    },
    [establishSession, unlockUsingSecret],
  );

  const signUp = useCallback(
    async (input: SignUpInput) => {
      const res = await apiSignup(input);
      setPendingPassword(input.password);
      await establishSession(res.token);
    },
    [establishSession],
  );

  const adoptToken = useCallback(
    async (token: string) => {
      setIsLoading(true);
      try {
        await establishSession(token);
      } finally {
        setIsLoading(false);
      }
    },
    [establishSession],
  );

  const signOut = useCallback(async () => {
    generation.current++;
    const current = userRef.current;
    if (current) await clearStoredDEK(current.id);
    clearToken();
    setUser(null);
    setPendingPassword(null);
    setVault({ status: 'loading' });
  }, []);

  const lockVault = useCallback(async () => {
    const current = userRef.current;
    if (current) await clearStoredDEK(current.id);
    const keys = await fetchVaultKeys().catch(() => null);
    setVault({
      status: 'locked',
      hasPasswordWrap: Boolean(keys?.passwordWrappedDek),
      hasRecoveryWrap: Boolean(keys?.recoveryWrappedDek),
    });
  }, []);

  const createVault = useCallback(async (password: string | null): Promise<string> => {
    const current = userRef.current;
    if (!current) throw new Error('Sign in before creating a vault.');

    const wraps = await provisionVault(password);
    await storeVaultKeys({
      passwordWrappedDek: wraps.passwordWrappedDek,
      recoveryWrappedDek: wraps.recoveryWrappedDek,
      dekSalt: wraps.dekSalt,
      rawDek: wraps.rawDek,
    });

    // Self-check before trusting the vault: unwrap what we just uploaded. Persisting the key only
    // after the server accepts the wraps means a failed upload can never leave this browser
    // writing entries that nothing else will ever be able to read.
    const unwrapped = await unwrapWithSecret(
      wraps.passwordWrappedDek,
      password ?? wraps.recoveryPhrase,
      wraps.dekSalt,
    );
    if (!unwrapped) throw new Error('Vault creation failed a self-check. Please try again.');

    const sessionKey = await toNonExtractable(unwrapped);
    await storeDEK(current.id, sessionKey);
    setPendingPassword(null); // no longer needed; don't keep it in memory longer than necessary
    // Not 'unlocked' yet: the phrase has to be shown and confirmed first, and holding it in the
    // vault state (rather than only in the screen) means a re-render cannot lose it.
    setVault({ status: 'needs-backup', dek: sessionKey, recoveryPhrase: wraps.recoveryPhrase });
    return wraps.recoveryPhrase;
  }, []);

  const acknowledgeRecoveryPhrase = useCallback(() => {
    setVault((current) =>
      current.status === 'needs-backup' ? { status: 'unlocked', dek: current.dek } : current,
    );
  }, []);

  const unlockWithPassword = useCallback(
    (password: string) => unlockUsingSecret(password, 'password'),
    [unlockUsingSecret],
  );

  const unlockWithRecoveryPhrase = useCallback(
    async (phrase: string) => {
      const canonical = normalizeRecoveryPhrase(phrase);
      if (!canonical) return false;
      return unlockUsingSecret(canonical, 'recovery');
    },
    [unlockUsingSecret],
  );

  const value = useMemo<SessionValue>(
    () => ({
      user,
      isLoading,
      vault,
      pendingPassword,
      signIn,
      signUp,
      adoptToken,
      signOut,
      createVault,
      acknowledgeRecoveryPhrase,
      unlockWithPassword,
      unlockWithRecoveryPhrase,
      lockVault,
    }),
    [
      user,
      isLoading,
      vault,
      pendingPassword,
      signIn,
      signUp,
      adoptToken,
      signOut,
      createVault,
      acknowledgeRecoveryPhrase,
      unlockWithPassword,
      unlockWithRecoveryPhrase,
      lockVault,
    ],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}
